import { useMemo, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useLanguage } from "../../../contexts/LanguageContext";
import { formatDay, formatNumber } from "../format";
import { Panel, SegmentedControl, Skeleton } from "./ui";

const METRICS = ["new_customers", "visits"];

// This period against the previous one, day by day on one axis. Day i of the
// current period is drawn over day i of the previous period, so the dashed
// line is "the same stretch, one period earlier".
export default function GrowthChart({ daily, dailyPrevious, rangeDays, loading }) {
  const { t, language } = useLanguage();
  const [metric, setMetric] = useState("new_customers");

  const data = useMemo(
    () =>
      (daily || []).map((day, index) => ({
        date: day.date,
        current: day[metric] ?? 0,
        previous: dailyPrevious?.[index]?.[metric] ?? null,
        previousDate: dailyPrevious?.[index]?.date ?? null,
      })),
    [daily, dailyPrevious, metric],
  );

  const metricLabel = t(`dashboard.overview.chart.metrics.${metric}`);

  return (
    <Panel
      title={t("dashboard.overview.chart.title")}
      className="flex-[2_1_560px]"
      actions={
        <SegmentedControl
          label={t("dashboard.overview.chart.metricLabel")}
          value={metric}
          onChange={setMetric}
          options={METRICS.map((m) => ({
            value: m,
            label: t(`dashboard.overview.chart.metrics.${m}`),
          }))}
        />
      }
    >
      <div className="-mt-2 flex flex-wrap gap-4 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-3.5 bg-indigo-600" />
          {t("dashboard.overview.chart.thisPeriod")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3.5 border-t-2 border-dashed border-gray-400" />
          {t("dashboard.overview.chart.previousPeriod")}
        </span>
      </div>
      {loading ? (
        <Skeleton className="h-60 w-full" />
      ) : (
        <div
          className="h-60 w-full"
          role="img"
          aria-label={t("dashboard.overview.chart.aria", {
            metric: metricLabel,
            days: rangeDays,
          })}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#F0F1F3" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => formatDay(d, language)}
                tick={{ fontSize: 11, fill: "#6B7280" }}
                tickLine={false}
                axisLine={{ stroke: "#E5E7EB" }}
                minTickGap={24}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#6B7280" }}
                tickLine={false}
                axisLine={false}
                width={44}
                tickFormatter={(v) => formatNumber(v, language)}
              />
              <Tooltip
                cursor={{ stroke: "#9CA3AF", strokeWidth: 1 }}
                content={<ChartTooltip metricLabel={metricLabel} />}
              />
              <Line
                type="monotone"
                dataKey="previous"
                stroke="#9CA3AF"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="current"
                stroke="#4F46E5"
                strokeWidth={2}
                fill="#4F46E5"
                fillOpacity={0.08}
                activeDot={{ r: 4, stroke: "#FFFFFF", strokeWidth: 2 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

function ChartTooltip({ active, payload, metricLabel }) {
  const { language } = useLanguage();
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="font-medium text-gray-900">{metricLabel}</p>
      <p className="mt-1 flex justify-between gap-4 text-gray-700">
        <span>{formatDay(point.date, language)}</span>
        <span className="font-semibold">{formatNumber(point.current, language)}</span>
      </p>
      {point.previousDate && (
        <p className="flex justify-between gap-4 text-gray-500">
          <span>{formatDay(point.previousDate, language)}</span>
          <span>{formatNumber(point.previous, language)}</span>
        </p>
      )}
    </div>
  );
}
